import {
  Box,
  Typography,
  Button,
  Container,
  Paper,
  TextField,
  Alert,
} from "@mui/material";
import EmailIcon from "@mui/icons-material/Email";
import GoogleIcon from "@mui/icons-material/Google";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "../routes/routes";
import { forgotPasswordAsync } from "../features/authentication/authenticationApi";

const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async () => {
    setEmailError("");
    setError("");

    if (!email || !email.includes("@")) {
      setEmailError("Please enter a valid email address");
      return;
    }

    setIsSubmitting(true);
    try {
      await forgotPasswordAsync(email);
      setIsSubmitted(true);
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenGmail = () => {
    window.open("https://mail.google.com", "_blank");
  };

  const handleOpenOutlook = () => {
    window.open("https://outlook.office.com/mail/", "_blank");
  };

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: `calc(100vh - var(--header-height, 72px))`,
        }}
      >
        <Paper
          elevation={0}
          sx={{ p: { xs: 3, sm: 5 }, width: "100%" }}
        >
          {!isSubmitted ? (
            <>
              <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: "50%",
                    background: "linear-gradient(135deg, rgba(14,134,212,0.08) 0%, rgba(26,155,230,0.08) 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <EmailIcon sx={{ width: 40, height: 40, color: "primary.main" }} />
                </Box>
              </Box>

              <Typography
                variant="h5"
                component="h1"
                align="center"
                gutterBottom
              >
                Forgot your password?
              </Typography>

              <Typography
                variant="body1"
                align="center"
                sx={{ mb: 3, color: "text.secondary" }}
              >
                Enter your email address and we'll send you a link to reset your
                password.
              </Typography>

              <TextField
                fullWidth
                label="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
                error={!!emailError}
                helperText={emailError}
                disabled={isSubmitting}
                sx={{ mb: 2 }}
              />

              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              <Button
                variant="contained"
                fullWidth
                size="large"
                onClick={handleSubmit}
                disabled={isSubmitting}
                sx={{ py: 1.5, mb: 2 }}
              >
                {isSubmitting ? "Sending..." : "Send Reset Link"}
              </Button>

              <Box sx={{ textAlign: "center" }}>
                <Typography
                  component="span"
                  variant="body2"
                  sx={{
                    color: "primary.main",
                    cursor: "pointer",
                    fontWeight: 500,
                    "&:hover": { textDecoration: "underline" },
                  }}
                  onClick={() => navigate(ROUTES.LOGIN)}
                >
                  Back to login
                </Typography>
              </Box>
            </>
          ) : (
            <>
              <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: "50%",
                    background: "linear-gradient(135deg, rgba(14,134,212,0.08) 0%, rgba(26,155,230,0.08) 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <EmailIcon sx={{ width: 40, height: 40, color: "primary.main" }} />
                </Box>
              </Box>

              <Typography
                variant="h5"
                component="h1"
                align="center"
                gutterBottom
              >
                Check your email
              </Typography>

              <Typography variant="body1" align="center" sx={{ mb: 3, color: "text.secondary" }}>
                If an account exists for
                <Box
                  component="span"
                  sx={{
                    ml: 1,
                    backgroundColor: "rgba(14,134,212,0.06)",
                    color: "primary.main",
                    fontWeight: 500,
                    px: 1.5,
                    py: 0.5,
                    borderRadius: 2,
                    display: "inline-block",
                    textAlign: "center",
                  }}
                >
                  {email}
                </Box>
                , we've sent a password reset link.
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  gap: 2,
                  mb: 3,
                }}
              >
                <Button
                  variant="outlined"
                  onClick={handleOpenGmail}
                  startIcon={<GoogleIcon />}
                  sx={{ px: 3 }}
                >
                  Open Gmail
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleOpenOutlook}
                  sx={{ px: 3 }}
                >
                  Open Outlook
                </Button>
              </Box>

              <Box sx={{ textAlign: "center" }}>
                <Typography
                  component="span"
                  variant="body2"
                  sx={{
                    color: "primary.main",
                    cursor: "pointer",
                    fontWeight: 500,
                    "&:hover": { textDecoration: "underline" },
                  }}
                  onClick={() => navigate(ROUTES.LOGIN)}
                >
                  Back to login
                </Typography>
              </Box>
            </>
          )}
        </Paper>
      </Box>
    </Container>
  );
};

export default ForgotPasswordPage;
